import {
  Alert,
  Button,
  Col,
  Collapse,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
} from "antd";
import { useEffect, useState } from "react";
import {
  createCityPricing,
  getCityPricingById,
  getRegionalPricing,
  parseInternationalPricingError,
  updateCityPricing,
} from "../../services/internationalPricingApi";
import { getCountries } from "../../services/countryApi";
import {
  CityPricing,
  CityPricingChanges,
  CityPricingPayload,
  RegionalPricing,
} from "../../shared/dtos/internationalPricingDTO";
import { Country } from "../../shared/dtos/country";
import {
  calculateCityPricingFromAirport,
  getChangedFields,
  normalizeSearchText,
  PRICE_TYPE_OPTIONS,
  REGION_OPTIONS,
  removeUndefined,
  validateCityPricingRanges,
} from "./utils";

interface Props {
  open: boolean;
  cityId?: number;
  onClose: () => void;
  onSaved: () => void;
}

const optionalNumberFields: Array<{
  name: keyof CityPricingPayload;
  label: string;
}> = [
  { name: "usdPerKmMin", label: "USD/KM mín" },
  { name: "usdPerKmMax", label: "USD/KM máx" },
  { name: "minimumFareFloorUsd", label: "Tarifa mín (piso)" },
  { name: "minimumFareCeilingUsd", label: "Tarifa mín (techo)" },
  { name: "cityMinimumCostUsd", label: "Costo mín ciudad" },
  { name: "minimumChargeUsd", label: "Cobro mínimo" },
];

const CityPricingForm = ({ open, cityId, onClose, onSaved }: Props) => {
  const [form] = Form.useForm<CityPricingPayload>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [original, setOriginal] = useState<CityPricingPayload>();
  const [error, setError] = useState<string>();
  const [countries, setCountries] = useState<Country[]>([]);
  const [regions, setRegions] = useState<RegionalPricing[]>([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(undefined);
    form.resetFields();
    setOriginal(undefined);
    setLoading(true);

    Promise.all([
      getCountries(),
      getRegionalPricing(),
      cityId ? getCityPricingById(cityId) : Promise.resolve(undefined),
    ])
      .then(([countryData, regionData, city]) => {
        if (cancelled) return;
        setCountries(
          [...countryData].sort((a, b) => a.name.localeCompare(b.name)),
        );
        setRegions(regionData);
        if (city) {
          const { id, ...values } = city as CityPricing;
          form.setFieldsValue(values);
          setOriginal(values);
        } else {
          form.setFieldsValue({ priceType: "automatic" });
        }
      })
      .catch((loadError: unknown) => {
        if (cancelled) return;
        setError(parseInternationalPricingError(loadError).message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [cityId, form, open]);

  const applyServerErrors = (errors?: Record<string, string | string[]>) => {
    if (!errors) return;
    form.setFields(
      Object.entries(errors).map(([name, messages]) => ({
        name: name as keyof CityPricingPayload,
        errors: Array.isArray(messages) ? messages : [messages],
      })),
    );
  };

  const submit = async (values: CityPricingPayload) => {
    const cleaned = removeUndefined(values);
    const rangeError = validateCityPricingRanges(cleaned);
    if (rangeError) {
      setError(rangeError);
      return;
    }

    setSaving(true);
    setError(undefined);
    try {
      if (cityId && original) {
        const changes = getChangedFields(
          cleaned as unknown as Record<string, unknown>,
          original as unknown as Record<string, unknown>,
        ) as CityPricingChanges;
        if (Object.keys(changes).length) {
          await updateCityPricing(cityId, changes);
        }
      } else {
        await createCityPricing(cleaned);
      }
      onSaved();
    } catch (saveError) {
      const parsed = parseInternationalPricingError(saveError);
      setError(parsed.message);
      applyServerErrors(parsed.errors);
    } finally {
      setSaving(false);
    }
  };

  const numberInput = (name: keyof CityPricingPayload, label: string) => (
    <Col xs={24} md={8} key={name}>
      <Form.Item name={name} label={label}>
        <InputNumber min={0} controls={false} style={{ width: "100%" }} />
      </Form.Item>
    </Col>
  );

  const handleValuesChange = (
    changedValues: Partial<CityPricingPayload>,
    values: CityPricingPayload,
  ) => {
    const shouldRecalculate = [
      "airportUsd",
      "airportKm",
      "regionKey",
    ].some((field) => Object.prototype.hasOwnProperty.call(changedValues, field));
    if (!shouldRecalculate) return;

    const region = regions.find(
      (item) => item.regionKey === values.regionKey,
    );
    const calculated = calculateCityPricingFromAirport(
      values.airportUsd,
      values.airportKm,
      region,
    );
    if (Object.keys(calculated).length) {
      form.setFieldsValue(calculated);
    }
  };

  return (
    <Modal
      open={open}
      title={cityId ? "Editar ciudad" : "Agregar ciudad"}
      onCancel={onClose}
      width={940}
      destroyOnHidden
      footer={null}
    >
      {error && (
        <Alert
          type="error"
          showIcon
          closable
          message={error}
          onClose={() => setError(undefined)}
        />
      )}
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        disabled={loading}
        onFinish={submit}
        onValuesChange={handleValuesChange}
      >
        <Row gutter={12}>
          <Col xs={24} md={6}>
            <Form.Item
              name="country"
              label="País"
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Seleccionar país"
                filterOption={(input, option) =>
                  normalizeSearchText(String(option?.label ?? "")).includes(
                    normalizeSearchText(input),
                  )
                }
                options={countries.map((country) => ({
                  value: country.name,
                  label: country.name,
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item
              name="city"
              label="Ciudad"
              rules={[{ required: true, whitespace: true }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item
              name="regionKey"
              label="Región"
              rules={[{ required: true }]}
            >
              <Select options={REGION_OPTIONS} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item
              name="priceType"
              label="Tipo de precio"
              rules={[{ required: true }]}
            >
              <Select options={PRICE_TYPE_OPTIONS} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item
              name="airportUsd"
              label="Aeropuerto USD"
              rules={[{ required: true }]}
            >
              <InputNumber min={0} controls={false} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="airportKm" label="KM al aeropuerto">
              <InputNumber min={0} controls={false} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        <Collapse
          ghost
          items={[
            {
              key: "advanced",
              label: "Parámetros avanzados",
              children: (
                <>
                  <Alert
                    type="info"
                    showIcon
                    message="Los valores se calculan con el precio del aeropuerto, sus kilómetros y los multiplicadores de la región. Puedes ajustarlos manualmente."
                  />
                  <Row gutter={12} style={{ marginTop: 12 }}>
                    {optionalNumberFields.map(({ name, label }) =>
                      numberInput(name, label),
                    )}
                  </Row>
                </>
              ),
            },
          ]}
        />

        <Row justify="end">
          <Space>
            <Button onClick={onClose}>Cancelar</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={saving}
              disabled={loading}
            >
              Guardar
            </Button>
          </Space>
        </Row>
      </Form>
    </Modal>
  );
};

export default CityPricingForm;
